const MinimumOrder = (() => {
  const DEFAULT_MINIMUM_CENTS = 5000;
  const EU_MINIMUM_CENTS = 7500;
  const EU_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
    'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  ];
  const ORDER_COMPONENTS = '[data-minimum-order]';
  const CHECKOUT_CONTROLS = '[data-minimum-order-checkout]';
  const ACCELERATED_CONTROLS = '[data-minimum-order-accelerated]';
  const REFRESH_EVENTS = [
    'cart:add',
    'cart:updated',
    'cart:update',
    'cartUpdate',
    'theme:cart:update',
    'cart:change',
    'cart:refresh',
    'minimum-order:refresh',
  ];

  let state = null;
  let refreshTimer = null;
  let exemptProductIdsPromise = null;

  // EU parcels carry customs and handling costs, so EU visitors have a higher minimum.
  const minimumCents = () => {
    const country = document.querySelector('[data-current-country]')?.dataset.currentCountry || window.Shopify?.country || '';
    return EU_COUNTRIES.includes(country) ? EU_MINIMUM_CENTS : DEFAULT_MINIMUM_CENTS;
  };

  const cartUrl = () => `${window.Shopify?.routes?.root || '/'}cart.js`;
  const exemptCollectionUrl = (page) => `${window.Shopify?.routes?.root || '/'}collections/recipes/products.json?limit=250&page=${page}`;

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'code',
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_) {
      return `USD ${(cents / 100).toFixed(2)}`;
    }
  };

  const replaceToken = (template, token, value) => String(template || '').replaceAll(token, value);

  const readCart = async () => {
    const response = await fetch(cartUrl(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const readExemptProductIds = async () => {
    if (exemptProductIdsPromise) return exemptProductIdsPromise;

    exemptProductIdsPromise = (async () => {
      const productIds = new Set();

      for (let page = 1; page <= 20; page += 1) {
        const response = await fetch(exemptCollectionUrl(page), { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Unable to load minimum-order exemptions');

        const payload = await response.json();
        const products = Array.isArray(payload?.products) ? payload.products : [];
        products.forEach((product) => productIds.add(String(product.id)));
        if (products.length < 250) break;
      }

      return productIds;
    })().catch((error) => {
      exemptProductIdsPromise = null;
      throw error;
    });

    return exemptProductIdsPromise;
  };

  const toState = (cart, exemptProductIds) => {
    const minimum = minimumCents();
    const cartSubtotal = Math.max(0, Number(cart?.total_price || 0));
    const itemCount = Number(cart?.item_count || 0);
    const minimumItems = (cart?.items || []).filter((item) => !exemptProductIds.has(String(item.product_id)));
    const minimumItemCount = minimumItems.reduce((count, item) => count + Number(item.quantity || 0), 0);
    const subtotal = minimumItems.reduce((total, item) => total + Math.max(0, Number(item.final_line_price || 0)), 0);
    const remaining = Math.max(0, minimum - subtotal);
    return {
      cart,
      itemCount,
      minimumItemCount,
      cartSubtotal,
      subtotal,
      minimum,
      remaining,
      percent: Math.min(100, Math.round((subtotal / minimum) * 100)),
      isBelowMinimum: minimumItemCount > 0 && remaining > 0,
      isEligible: minimumItemCount > 0 && remaining === 0,
    };
  };

  const getDismissalKey = (currentState) => `minimumOrderDismissed:${currentState.cart?.token || 'cart'}:${currentState.subtotal}`;

  const isDismissed = (currentState) => {
    try {
      return sessionStorage.getItem(getDismissalKey(currentState)) === 'true';
    } catch (_) {
      return false;
    }
  };

  const dismiss = (currentState) => {
    try {
      sessionStorage.setItem(getDismissalKey(currentState), 'true');
    } catch (_) {
      // Session storage is optional.
    }
  };

  const setComponentState = (component, currentState) => {
    const context = component.dataset.minimumOrderContext;
    const shouldHide = currentState.minimumItemCount === 0 || (context === 'floating' && (!currentState.isBelowMinimum || isDismissed(currentState)));
    component.hidden = shouldHide;
    component.dataset.minimumOrderState = currentState.isEligible ? 'met' : 'shortfall';
    if (shouldHide) return;

    const policy = component.querySelector('[data-minimum-order-policy]');
    const message = component.querySelector('[data-minimum-order-message]');
    const percent = component.querySelector('[data-minimum-order-percent]');
    const fill = component.querySelector('[data-minimum-order-fill]');
    const track = component.querySelector('[data-minimum-order-track]');
    const amount = formatMoney(currentState.minimum);
    const remaining = formatMoney(currentState.remaining);

    if (policy) policy.textContent = replaceToken(component.dataset.minimumOrderPolicy, '__amount__', amount);
    if (message) {
      message.textContent = currentState.isEligible
        ? component.dataset.minimumOrderReached
        : replaceToken(component.dataset.minimumOrderRemaining, '__amount__', remaining);
    }
    if (percent) percent.textContent = `${currentState.percent}%`;
    if (fill) fill.style.width = `${currentState.percent}%`;
    if (track) {
      track.setAttribute('aria-valuenow', String(currentState.percent));
      track.setAttribute('aria-label', replaceToken(component.dataset.minimumOrderProgressAria, '__percent__', currentState.percent));
    }
  };

  const setCheckoutState = (currentState) => {
    document.querySelectorAll(CHECKOUT_CONTROLS).forEach((control) => {
      if (!control.dataset.minimumOrderOriginalLabel) {
        control.dataset.minimumOrderOriginalLabel = control.textContent.trim();
      }

      control.classList.toggle('minimum-order-checkout--blocked', currentState.isBelowMinimum);
      control.dataset.minimumOrderBlocked = currentState.isBelowMinimum ? 'true' : 'false';

      if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
        control.disabled = currentState.isBelowMinimum || currentState.itemCount === 0;
      } else {
        control.setAttribute('aria-disabled', currentState.isBelowMinimum ? 'true' : 'false');
      }

      if (currentState.isBelowMinimum) {
        const component = document.querySelector(ORDER_COMPONENTS);
        const template = component?.dataset.minimumOrderRemaining || '';
        control.textContent = replaceToken(template, '__amount__', formatMoney(currentState.remaining));
      } else {
        control.textContent = control.dataset.minimumOrderOriginalLabel;
      }
    });

    document.querySelectorAll(ACCELERATED_CONTROLS).forEach((control) => {
      control.hidden = currentState.isBelowMinimum;
    });
  };

  const render = (currentState) => {
    document.querySelectorAll(ORDER_COMPONENTS).forEach((component) => setComponentState(component, currentState));
    setCheckoutState(currentState);
  };

  const announceShortfall = (origin) => {
    const component = origin.closest('.nds-cart-drawer, .cart__summary, .cart__ctas')?.querySelector(ORDER_COMPONENTS) || document.querySelector(ORDER_COMPONENTS);
    const message = component?.querySelector('[data-minimum-order-message]');
    if (!message) return;
    component.hidden = false;
    message.focus({ preventScroll: false });
  };

  const refresh = async () => {
    try {
      const [cart, exemptProductIds] = await Promise.all([readCart(), readExemptProductIds()]);
      state = toState(cart, exemptProductIds);
      render(state);
      document.dispatchEvent(new CustomEvent('minimum-order:updated', { detail: state }));
    } catch (_) {
      // Leave native checkout controls available when the client cannot verify cart state.
      state = null;
    }
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, 120);
  };

  const interceptCartFetches = () => {
    const originalFetch = window.fetch;
    if (originalFetch.__minimumOrderWrapped) return;

    const wrappedFetch = function wrappedFetch(...args) {
      const request = args[0];
      const url = typeof request === 'string' ? request : request?.url || '';
      const response = originalFetch.apply(this, args);
      if (/\/cart\/(add|change|update|clear)\.js/i.test(url)) {
        response.then(() => scheduleRefresh()).catch(() => {});
      }
      return response;
    };

    wrappedFetch.__minimumOrderWrapped = true;
    window.fetch = wrappedFetch;
  };

  const bind = () => {
    REFRESH_EVENTS.forEach((eventName) => document.addEventListener(eventName, scheduleRefresh));
    interceptCartFetches();

    document.addEventListener('click', (event) => {
      const dismissButton = event.target.closest('[data-minimum-order-dismiss]');
      if (dismissButton && state) {
        dismiss(state);
        dismissButton.closest(ORDER_COMPONENTS).hidden = true;
        return;
      }

      const checkout = event.target.closest(CHECKOUT_CONTROLS);
      if (!checkout || !state?.isBelowMinimum) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      announceShortfall(checkout);
    }, true);

    document.addEventListener('submit', (event) => {
      if (!state?.isBelowMinimum) return;
      const submitter = event.submitter;
      if (!submitter?.matches(CHECKOUT_CONTROLS)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      announceShortfall(submitter);
    }, true);
  };

  const init = () => {
    bind();
    refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  return { refresh };
})();

export default MinimumOrder;
